import { useEffect, useMemo } from "react";
import { formatDistance } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import RuleAction from "../../actions/RuleAction";
import RuleItemBuilder from "./RuleItemBuilder";
import Trash from "../../icons/Trash";
import PlusIcon from "../../icons/Plus";
import Checkbox from "../../components/Checkbox";
import DropdownLayout from "../../components/DropdownLayout";
import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { RuleItemTypes } from "../../constants/action-types";
import { fromJS } from "immutable";
import { useTranslation } from "react-i18next";
import useUpgradePlan from "../../team/hooks/useUpgradePlan";
import ProjectAction from "../../actions/ProjectAction";

export const RuleList = ({ setup }: any) => {
  const { t } = useTranslation();
  const { projectId }: any = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { invalidFeature } = useUpgradePlan();

  const rules = useSelector((state: any) => state.ruleReducer.get("rules"));
  const members = useSelector((state: any) => state.projectReducer.getIn(["project", "members"]));
  const automationUsage = useSelector((state: any) => state.projectReducer.get("automationUsage"));

  useEffect(() => {
    if (projectId) {
      dispatch(RuleAction.getRules(projectId));
    }
  }, [projectId, dispatch]);

  useEffect(() => {
    if (projectId) {
      dispatch(ProjectAction.getWorkspaceAutomationUsage(projectId));
    }
  }, [projectId, rules?.size, dispatch]);

  const handleDelete = async (ruleId) => {
    await dispatch(RuleAction.deleteRule(ruleId));
    dispatch(RuleAction.getRules(projectId));
  };

  const showAddRuleForm = () => {
    if (invalidFeature("automation")) return;
    navigate(`/workspaces/${projectId}/automations/new`);
  };

  const handleEnableRule = async (ruleId) => {
    await dispatch(RuleAction.enableRule(ruleId));
    dispatch(RuleAction.getRules(projectId));
  };

  const formatDate = (dateArray: any) => {
    const dateObject = new Date(dateArray);
    return formatDistance(dateObject, Date.now());
  };

  const renderRuleDataItem = (ruleItem, index, type = RuleItemTypes.EVENT) => {
    return (
      <RuleItemBuilder
        rule={fromJS({})}
        key={`${ruleItem.key}_${index}`}
        readonly={true}
        onChange={() => {}}
        value={ruleItem}
        itemType={type}
      />
    );
  };

  const renderStatus = (ruleData) => {
    return (
      <Checkbox
        checked={ruleData.get("enabled")}
        onChange={async () => {
          await handleEnableRule(ruleData.get("id"));
        }}
      />
    );
  };

  const renderActions = (ruleItem: any) => {
    if (!setup) {
      return (
        <DropdownLayout
          fullWidth={false}
          positionClassName={"right-0 origin-top-right"}
          activator={<EllipsisHorizontalIcon className={"w-6"} />}
        >
          <ul
            className={"p-2 cursor-pointer hover:text-blue-950 flex w-24 h-10"}
            onClick={async () => {
              await handleDelete(ruleItem.get("id"));
            }}
          >
            <li className={"flex w-full hover:text-red-600 items-center"}>
              <Trash className={"w-4"} />
              <div>{t("Delete")}</div>
            </li>
          </ul>
        </DropdownLayout>
      );
    }
  };

  const handleOnClickItem = (id) => {
    const url = `/workspaces/${projectId}/automations/${id}`;
    if (!setup) {
      navigate(url);
    }
  };

  const renderRuleItem = (ruleItem: any) => {
    const { events = [], actions = [], updatedAt = [], id, createdBy } = ruleItem.toJS();
    const createdByUser =
      members.find((item) => item.get("accountId") == createdBy) ||
      fromJS({ displayName: "System" });
    return (
      <div
        key={`rule_${id}`}
        onClick={() => {
          handleOnClickItem(id);
        }}
        className={"cursor-pointer flex w-full shadow-sm p-4 hover:bg-gray-50"}
      >
        <div className={"w-full"}>
          <div className={"flex w-full"}>
            <div className={"space-y-2 w-64 truncate ..."}>
              {events.map((ruleEvent, index) =>
                renderRuleDataItem(ruleEvent, index, RuleItemTypes.EVENT)
              )}
            </div>
            <div className={"w-1 px-2"}>,</div>
            <div className={"space-y-2"}>
              {actions.map((ruleAction, index) =>
                renderRuleDataItem(ruleAction, index, RuleItemTypes.ACTION)
              )}
            </div>
          </div>
        </div>
        <div className={"w-auto"} onClick={(e) => e.stopPropagation()}>
          <div className={"flex space-x-1 justify-end"}>
            {renderStatus(ruleItem)}
            {renderActions(ruleItem)}
          </div>
          <div className={"text-gray-400 text-sm whitespace-nowrap"}>
            <div className={"flex justify-end"}>
              <div>{createdByUser.get("displayName")}</div>
              <div className={"px-1"}>-</div>
              <div>{t("Updated {{time}} ago", { time: formatDate(updatedAt) })}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRuleTable = () => {
    if (rules.size == 0) {
      return null;
    }
    return (
      <div className={"w-full space-y-2"}>
        {rules.map((ruleItem: any) => renderRuleItem(ruleItem))}
      </div>
    );
  };

  const usageProgress = useMemo(() => {
    const limit = automationUsage?.get("limit");
    const executedCount = automationUsage?.get("executedCount") || 0;
    if (!limit || limit <= 0) return null;
    return Math.min(100, Math.round((executedCount / limit) * 100));
  }, [automationUsage]);

  const renderAutomationUsage = () => {
    if (!automationUsage) return null;
    const overQuota = automationUsage.get("blocked");
    const limit = automationUsage.get("limit");
    const executedCount = automationUsage.get("executedCount") || 0;
    const remaining = automationUsage.get("remaining");
    const isUnlimited = limit == null;
    const usageText = isUnlimited
      ? t("{{count}} runs this month (unlimited)", { count: executedCount })
      : t("{{count}} / {{limit}} runs this month", {
          count: executedCount,
          limit: limit,
        });
    const remainingText =
      !isUnlimited && remaining != null
        ? t("{{count}} runs remaining", { count: remaining })
        : t("No monthly limit");
    return (
      <div
        className={`my-3 rounded-md border px-3 py-2 ${
          overQuota ? "border-red-200 bg-red-50" : "border-blue-200 bg-blue-50"
        }`}
      >
        <div className={"flex items-start justify-between gap-2"}>
          <div>
            <div className={"text-sm font-semibold text-gray-800"}>{t("Automation Usage")}</div>
            <div className={"text-sm text-gray-700"}>{usageText}</div>
            <div className={"text-xs text-gray-600"}>{remainingText}</div>
          </div>
          {overQuota && (
            <div className={"flex items-center gap-1 text-red-700 text-sm font-medium"}>
              <ExclamationTriangleIcon className={"w-4 h-4"} />
              <span>{t("Over quota")}</span>
            </div>
          )}
        </div>
        {usageProgress != null && (
          <div className={"mt-2 h-2 w-full rounded bg-white"}>
            <div
              className={`h-2 rounded ${overQuota ? "bg-red-500" : "bg-blue-500"}`}
              style={{ width: `${usageProgress}%` }}
            />
          </div>
        )}
        {overQuota && (
          <div className={"mt-2 text-xs text-red-700"}>
            {t(
              "Automation is over the monthly quota. New automation runs are blocked for this workspace until the limit is increased or the next month starts."
            )}
          </div>
        )}
      </div>
    );
  };

  const renderRules = () => {
    const activeRules = rules.filter((ruleItem) => ruleItem.get("enabled")).size;
    return (
      <div className={"p-2 w-full"}>
        <div className={"w-full flex border-b border-b-gray-200"}>
          <div className={"float-left flex align-middle w-full space-x-2"}>
            <div className={"font-semibold text-blue-600"}>{activeRules}</div>
            <div>{t("Active automations")}</div>
          </div>

          <div className={"float-right flex items-center space-x-2"}>
            {!setup && (
              <div
                onClick={showAddRuleForm}
                className="w-44 flex items-start gap-x-1 text-blue-600 p-2 font-semibold cursor-pointer"
              >
                <PlusIcon />
                {t("New Automation")}
              </div>
            )}
          </div>
        </div>
        {renderAutomationUsage()}
        {renderRuleTable()}
      </div>
    );
  };
  return renderRules();
};
